// src/components/NotificationProfileUpdateResultBadges.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { API_URL } from "config";

/* ───── Global singletons (safe in HMR) ───── */
const __g = typeof window !== "undefined" ? window : globalThis;

if (!__g.__profileResultSingleton) {
  __g.__profileResultSingleton = {
    counter: 0,
    listeners: new Set(),
    loaded: false,
    loading: false,
  };
}
if (!__g.__profileResultAudio) {
  __g.__profileResultAudio = new Audio("https://actions.google.com/sounds/v1/alarms/alarm_clock.ogg");
}

/* Local shorthands */
let __profileResult_counter = __g.__profileResultSingleton.counter;
let __profileResult_listeners = __g.__profileResultSingleton.listeners;

/* ───── Poller: hits user result-notifications every 10s ───── */
const __startProfileResultPolling = () => {
  if (__g.__profileResultSingleton.loaded || __g.__profileResultSingleton.loading) return;
  __g.__profileResultSingleton.loading = true;

  const token = localStorage.getItem("authToken") || "";
  const headers = { Authorization: `Bearer ${token}` };

  const publish = (val) => {
    if (val !== __profileResult_counter) {
      __profileResult_counter = val;
      __g.__profileResultSingleton.counter = val;
      __profileResult_listeners.forEach((fn) => {
        try { fn(__profileResult_counter); } catch { }
      });
    }
  };

  const fetchOnce = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/user/profile-update-result-notifications?_=${Date.now()}`,
        { headers }
      );

      // API can return array in different wrappers; normalize:
      const arr = res?.data?.notifications ?? res?.data?.data ?? res?.data ?? [];
      publish(Array.isArray(arr) ? arr.length : 0);
    } catch {
      // Optional fallback endpoint if you expose a /count API:
      try {
        const resCount = await axios.get(
          `${API_URL}/user/profile-update-result-notifications/count?_=${Date.now()}`,
          { headers }
        );
        const raw = resCount?.data?.count;
        const fallback = typeof raw === "number" ? raw : parseInt(raw, 10) || 0;
        publish(fallback);
      } catch {
        // keep last value
      }
    } finally {
      __g.__profileResultSingleton.loaded = true;
      __g.__profileResultSingleton.loading = false;
    }
  };

  fetchOnce();
};

const __subscribeProfileResult = (fn) => {
  __profileResult_listeners.add(fn);
  try { fn(__g.__profileResultSingleton.counter); } catch { }
  __startProfileResultPolling();
  return () => __profileResult_listeners.delete(fn);
};

/* ───── Parent Badge (NO ICON) : plays loop while count > 0 ───── */
const NotificationProfileUpdateResultBadgeIcon = ({ playSound, boxSize }) => {
  const [count, setCount] = useState(__g.__profileResultSingleton.counter);

  const audio = useMemo(() => __g.__profileResultAudio, []);
  const unsubRef = useRef(null);

  useEffect(() => {
    unsubRef.current = __subscribeProfileResult((newCount) => {
      setCount(newCount);

      if (!playSound) return;

      if (newCount > 0) {
        try {
          audio.loop = true;
          if (audio.paused) audio.play().catch(() => { });
        } catch { }
      } else {
        try {
          audio.pause();
          audio.currentTime = 0;
          audio.loop = false;
        } catch { }
      }
    });

    return () => {
      if (unsubRef.current) unsubRef.current();
      // Unmount par audio force-stop NAHI kar rahe — dusre badges bhi ho sakte.
    };
  }, [audio, playSound]);

  return (
    <div
      style={{
        position: "relative",
        display: "inline-block",
        width: boxSize,
        height: boxSize,
      }}
    >
      {/* No base icon anymore — only badge */}
      {count > 0 && (
        <span
          style={{
            position: "absolute",
            top: -2,
            right: -2,
            minWidth: 16,
            height: 16,
            padding: "0 6px",
            borderRadius: 999,
            background: "#e53935",
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            lineHeight: "16px",
            textAlign: "center",
            animation: "pulse 1s infinite",
          }}
          aria-label={`Profile updates result pending: ${count}`}
          title={`${count}`}
        >
          {count}
        </span>
      )}
      <style>
        {`@keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.15); }
          100% { transform: scale(1); }
        }`}
      </style>
    </div>
  );
};

NotificationProfileUpdateResultBadgeIcon.propTypes = {
  playSound: PropTypes.bool,
  boxSize: PropTypes.number, // container size to position the badge
};

NotificationProfileUpdateResultBadgeIcon.defaultProps = {
  playSound: true,
  boxSize: 20, // thoda sa box so badge position stable rahe
};

/* ───── Child Inline Badge: silent chip style (number only) ───── */
export const ProfileUpdateResultInlineBadge = ({ className = "", style = {} }) => {
  const [count, setCount] = useState(__g.__profileResultSingleton.counter);

  useEffect(() => {
    const off = __subscribeProfileResult((newCount) => setCount(newCount));
    return () => off();
  }, []);

  if (count <= 0) return null;

  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 16,
        height: 16,
        padding: "0 6px",
        fontSize: 10,
        lineHeight: "16px",
        borderRadius: 8,
        background: "#e53935",
        color: "#fff",
        fontWeight: 700,
        marginLeft: 6,
        ...style,
      }}
      aria-label={`Profile updates result pending: ${count}`}
      title={`${count}`}
    >
      {count}
    </span>
  );
};

ProfileUpdateResultInlineBadge.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
};

export default NotificationProfileUpdateResultBadgeIcon;
